import type { CSSProperties, ReactNode } from 'react';
import { cn } from '../lib/utils';

export interface ExternalTaskItem {
    id: string;
    /** Le nom de la tâche (ou de l'avis de valeur, etc.). */
    label: string;
    /** Contexte secondaire, par exemple le nom du projet qui la porte. */
    sublabel?: string | null;
    /**
     * Ouvre l'application cible, sur le bon enregistrement si elle le permet.
     * Absente pour un message sans lien (une actualité ajoutée à la main sans
     * URL, par exemple) : la ligne reste alors un simple texte, sans action.
     */
    url?: string | null;
    /**
     * `style` sert aux couleurs éditables côté source (ex. la couleur de
     * statut d'AVV, choisie librement par un administrateur) : une classe
     * Tailwind ne peut pas porter une couleur arbitraire connue seulement à
     * l'exécution.
     */
    badge?: { text: string; className?: string; style?: CSSProperties } | null;
}

interface ExternalTaskPanelProps {
    title: string;
    icon: ReactNode;
    items: ExternalTaskItem[];
    /**
     * Classes supplémentaires sur le conteneur. Sert au bandeau d'actualités de
     * l'accueil, qui fixe une hauteur pour occuper toute la colonne : la liste
     * défile alors elle-même dans cette hauteur, sans faire grandir la page.
     */
    className?: string;
    /**
     * Lignes plus resserrées. Sert au même bandeau, pour que davantage de
     * messages tiennent sans agrandir sa largeur. Les cartes Projet/AVV, elles,
     * gardent leur gabarit habituel : leur taille a déjà été ajustée à part.
     */
    dense?: boolean;
}

/**
 * Carte de raccourcis vers une autre application du parc, lue en direct dans
 * sa base par le Hub (voir docs/SECURITE.md, section 3, et le patron déjà
 * documenté côté Projet dans `docs/CRA_INTEGRATION.md`).
 *
 * Chaque ligne ouvre l'application cible dans un nouvel onglet, jamais une
 * route interne du Hub : celui-ci n'est pas un mandataire, il affiche ce
 * qu'il a lu chez l'autre application et rien de plus.
 */
export function ExternalTaskPanel({ title, icon, items, className, dense }: ExternalTaskPanelProps) {
    return (
        <section className={cn('card flex flex-col', className)}>
            <h2
                className={cn(
                    'flex items-center gap-2 px-4 text-sm font-semibold text-[var(--color-text-secondary)] border-b border-[var(--color-border)] shrink-0',
                    dense ? 'py-1.5' : 'py-2'
                )}
            >
                {icon}
                {title}
            </h2>
            <ul className="divide-y divide-[var(--color-border)] overflow-y-auto">
                {items.map((item) => {
                    const contenu = (
                        <>
                            <span className="flex-1 min-w-0">
                                <span
                                    className={cn(
                                        'block font-medium text-[var(--color-text)] truncate',
                                        dense ? 'text-xs' : 'text-sm',
                                        item.url && 'group-hover:text-[var(--color-primary)]'
                                    )}
                                >
                                    {item.label}
                                </span>
                                {item.sublabel && (
                                    <span className="block text-xs text-[var(--color-text-muted)] truncate">
                                        {item.sublabel}
                                    </span>
                                )}
                            </span>
                            {item.badge && (
                                <span
                                    className={
                                        item.badge.className ??
                                        'shrink-0 text-xs font-medium px-2 py-0.5 rounded-full bg-[var(--color-bg-hover)] text-[var(--color-text-secondary)]'
                                    }
                                    style={item.badge.style}
                                >
                                    {item.badge.text}
                                </span>
                            )}
                        </>
                    );

                    const padding = dense ? 'px-3 py-1' : 'px-4 py-1.5';

                    return (
                        <li key={item.id}>
                            {item.url ? (
                                <a
                                    href={item.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={cn(
                                        'flex items-center gap-3 hover:bg-[var(--color-bg-hover)] transition-colors group',
                                        padding
                                    )}
                                >
                                    {contenu}
                                </a>
                            ) : (
                                <div className={cn('flex items-center gap-3', padding)}>{contenu}</div>
                            )}
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}
